import { ConnectedPosition, Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { Injectable, InjectionToken, inject } from '@angular/core';

import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';

import { LocalStorageService } from '@core/common-legacy';
import type { ColorMenuController, OverlayPosition } from '@core/prosemirror-legacy';
import { WysiwygComponentPortalService } from '@core/prosemirror-legacy/angular';

import { ColorMenuComponent } from './color-menu.component';

const presetColors = [
  '#121317',
  '#5A5F72',
  '#7A7E8D',
  '#9A9DA9',
  '#E50101',
  '#F09A0F',
  '#147AB6',
  '#1999E3',
  '#7148BD',
  '#E83393',
  '#31BD95',
  '#1AB616',
];

export const PM_COLOR_SERVICE_FOR_TEXT_COLOR = new InjectionToken<ColorMenuService>(
  'ColorMenuServiceForTextColor',
);

export const PM_COLOR_SERVICE_FOR_TEXT_BACKGROUND = new InjectionToken<ColorMenuService>(
  'ColorMenuServiceForTextBackground',
);

export const colorMenuServiceProvider = (token: InjectionToken<ColorMenuService>) => ({
  provide: token,
  useFactory: () => new ColorMenuService(inject(OverlayContainer), inject(Overlay)),
});

@Injectable()
export class ColorMenuService
  extends WysiwygComponentPortalService<ColorMenuComponent>
  implements ColorMenuController
{
  private readonly overlayContainer = inject(OverlayContainer);
  private readonly LS_KEY = 'pm-colors-history';
  protected readonly ls = inject(LocalStorageService);

  protected overlayConfig = {
    hasBackdrop: true,
    backdropClass: 'cdk-overlay-transparent-backdrop',
  };
  protected closed$ = new Subject<{ value: string | null }>();

  public open(
    position: OverlayPosition | DOMRect,
    _options: {
      parent: HTMLElement;
      onChange: (value: string | null) => void;
      preferredPositions?: ConnectedPosition[];
      initialValue: string | null;
    },
  ) {
    const options = {
      preferredPositions: _options.preferredPositions || [
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
      ],
    };
    this.close();
    this.openComponent(ColorMenuComponent, position, options.preferredPositions, {
      skipComponentCD: true,
    });
    this.setInstanceProps(_options);
    const { value } = this.componentRef!.instance;
    this.overlayRef!.backdropClick()
      .pipe(take(1))
      .subscribe(() => {
        this.storeInHistory(value);
        this.closed$.next({ value });
        this.close();
      });

    this.componentRef?.changeDetectorRef.detectChanges();

    return {
      element: <HTMLElement>this.componentRef!.location.nativeElement,
      closed$: this.closed$.pipe(take(1)),
    };
  }

  private setInstanceProps(options: {
    initialValue: string | null;
    onChange: (value: string | null) => void;
  }) {
    const { instance } = this.componentRef!;
    const historyColors = this.getStoredColors();

    instance.presetColors = [...presetColors];
    instance.historyColors = [...historyColors];
    if (options.initialValue) {
      instance.value = options.initialValue;
    }
    instance.close = () => {
      this.close();
    };
    instance.reset = () => {
      this.closed$.next({ value: null });
      this.close();
    };
    instance.colorChanged = options.onChange;
  }

  private storeInHistory(value: string) {
    const history = this.getStoredColors();
    if (history.includes(value) || presetColors.includes(value)) return;
    const newHistory = [value, ...history.slice(0, 5)];
    this.ls.set(this.LS_KEY, newHistory);
  }

  private getStoredColors(): string[] {
    return this.ls.get(this.LS_KEY, ['', '', '', '', '', '']);
  }
}
